import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * GDPR consent card (Anexa 2 Sec. 3). Rendered when the backend's
 * request_consent tool fires; the composer is locked by the widget until the
 * user decides. Both links open in a new tab. The decision is emitted as a
 * `consent-decision` event - the widget POSTs it to the server-authoritative
 * /consent endpoint before the conversation resumes.
 */
@customElement('urban-consent-card')
export class ConsentCard extends LitElement {
  static override styles = css`
    :host {
      display: block;
      margin: 8px 0;
      animation: bf-consent-in 0.22s ease-out both;
    }
    @keyframes bf-consent-in {
      from { opacity: 0; transform: translateY(-6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      :host { animation: none; }
    }
    .wrap {
      background: var(--urban-bg-soft, #1a1a1a);
      border: 1px solid var(--urban-border, #E5E7EB);
      border-left: 3px solid var(--urban-accent, #8FFE09);
      border-radius: 12px;
      padding: 14px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .text {
      font-size: 13px;
      line-height: 1.55;
      color: var(--urban-text, #475467);
      margin: 0;
      white-space: pre-line;
    }
    .links {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 16px;
      font-size: 12px;
    }
    .links a {
      color: var(--urban-heading, #1D2939);
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .links a:hover { color: var(--urban-accent, #ff650c); }
    .actions {
      display: flex;
      gap: 8px;
    }
    button {
      flex: 1;
      padding: 10px 14px;
      border-radius: 8px;
      font: inherit;
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    button.accept {
      background: var(--urban-accent, #ff650c);
      color: #0a0a0a;
      border: 2px solid var(--urban-accent, #ff650c);
    }
    button.accept:hover {
      background: #ffffff;
      color: #0a0a0a;
      border-color: #ffffff;
    }
    button.decline {
      background: var(--urban-bg-soft, #1a1a1a);
      color: var(--urban-text, #475467);
      border: 2px solid var(--urban-border, #E5E7EB);
    }
    button.decline:hover {
      border-color: var(--urban-muted, #98A2B3);
      color: var(--urban-heading, #1D2939);
    }
    .decided {
      background: var(--urban-bg-soft, #F9FAFB);
      border-left: 3px solid var(--urban-accent, #8FFE09);
      padding: 12px 14px;
      border-radius: 10px;
      font-size: 13px;
      color: var(--urban-text, #475467);
    }
    .decided strong { color: var(--urban-heading, #1D2939); }
  `;

  @property({ type: String }) text = '';
  @property({ type: String }) acceptLabel = 'Sunt de acord';
  @property({ type: String }) declineLabel = 'Nu acum';
  @property({ type: String }) tcUrl = '';
  @property({ type: String }) privacyUrl = '';
  @property({ type: String }) language = 'ro';
  @state() private decided: 'accepted' | 'declined' | '' = '';

  private decide(decision: 'accepted' | 'declined') {
    if (this.decided) return;
    this.decided = decision;
    this.dispatchEvent(
      new CustomEvent('consent-decision', {
        detail: { decision },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    if (this.decided) {
      const msg =
        this.decided === 'accepted'
          ? this.language === 'en'
            ? html`<strong>Thank you!</strong> We can continue.`
            : html`<strong>Mulțumesc!</strong> Continuăm.`
          : this.language === 'en'
            ? html`No problem - I can still answer general questions. <strong>You can accept anytime later.</strong>`
            : html`Nicio problemă - îți răspund în continuare la întrebări generale. <strong>Poți accepta oricând mai târziu.</strong>`;
      return html`<div class="decided">${msg}</div>`;
    }
    const tcLabel = this.language === 'en' ? 'Terms and Conditions' : 'Termeni și Condiții';
    const ppLabel = this.language === 'en' ? 'Privacy Policy' : 'Politica de Confidențialitate';
    return html`
      <div class="wrap">
        <p class="text">${this.text}</p>
        <div class="links">
          <a href=${this.tcUrl} target="_blank" rel="noopener noreferrer">${tcLabel}</a>
          <a href=${this.privacyUrl} target="_blank" rel="noopener noreferrer">${ppLabel}</a>
        </div>
        <div class="actions">
          <button type="button" class="accept" @click=${() => this.decide('accepted')}>
            ${this.acceptLabel}
          </button>
          <button type="button" class="decline" @click=${() => this.decide('declined')}>
            ${this.declineLabel}
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'urban-consent-card': ConsentCard;
  }
}
